import { LOGO_ACCEPTED_TYPES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, toast } from "@sagot/ui";
import { useEffect, useMemo, useRef } from "react";
import { logoProblem } from "../features/workspace/settings/panels/logo-upload";

export { uploadLogo } from "../features/workspace/settings/panels/logo-upload";

const ACCEPT = LOGO_ACCEPTED_TYPES.join(",");

interface LogoPickerProps {
  /** Uploaded logo URL (existing workspace). */
  url: string | null;
  /** File chosen before the workspace exists (uploaded right after creation). */
  pending: File | null;
  busy: boolean;
  orgName: string;
  onPick: (file: File) => void;
  onRemove: () => void;
}

/** Onboarding step 1 logo: preview + pick/replace/remove (validation mirrors the API). */
export function LogoPicker({ url, pending, busy, orgName, onPick, onRemove }: LogoPickerProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const preview = useMemo(() => (pending ? URL.createObjectURL(pending) : null), [pending]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const shown = preview ?? url;
  const pick = (file: File | undefined) => {
    if (!file) return;
    const problem = logoProblem(file);
    if (problem) {
      toast.error(t(problem === "type" ? "onboarding.org.logoBadType" : "onboarding.org.logoTooLarge"));
      return;
    }
    onPick(file);
  };
  return (
    <div data-testid="logo-picker">
      <span className="label">{t("onboarding.org.logo")}</span>
      <div className="flex h-[164px] w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-line-strong bg-surface-2 px-3 text-center">
        {shown ? (
          <img src={shown} alt={t("onboarding.org.logoAlt", { name: orgName })} className="size-16 rounded-lg border border-line object-cover" />
        ) : (
          <span className="inline-flex size-10 items-center justify-center rounded-pill bg-surface text-primary-ink shadow-xs">
            <Icon name="upload-cloud" className="size-5" />
          </span>
        )}
        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="secondary" size="sm" loading={busy} onClick={() => input.current?.click()}>
            {t(shown ? "onboarding.org.logoReplace" : "onboarding.org.logoUpload")}
          </Button>
          {shown ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onRemove}>
              {t("onboarding.org.logoRemove")}
            </Button>
          ) : null}
        </div>
        <span className="text-xs text-fg-subtle">{pending ? t("onboarding.org.logoPending") : t("onboarding.org.logoHint")}</span>
      </div>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-label={t("onboarding.org.logoUpload")}
        data-testid="logo-input"
        onChange={(event) => {
          pick(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </div>
  );
}
